import { useEffect, useMemo, useState } from 'react'
import { capabilitiesErrorAtom } from 'atoms/capabilities'
import { useSetAtom } from 'jotai'
import { DEFAULT_ACCOUNT_CAPABILITIES } from 'constants/General'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { AccountCapabilities } from 'types/capabilities/capabilities'
import { Profile } from 'types/types'
import { getAccountCapabilities, getAccountCapabilityRequestFilters } from 'utils/capabilities'

interface AppProfileContext {
  data: Profile
  isLoading: boolean
}

export const useAccountCapabilities = (
  accountReference: string,
  profileContext: AppProfileContext
) => {
  const setCapabilitiesError = useSetAtom(capabilitiesErrorAtom)
  const { data: profile, isLoading: isProfileLoading } = profileContext || {}
  const [accountCapabilities, setAccountCapabilities] = useState<AccountCapabilities>(
    DEFAULT_ACCOUNT_CAPABILITIES
  )

  const accountCapabilityRequest = useMemo(() => {
    const isEnabled = !!accountReference && !isProfileLoading && profile?.loggedIn

    if (isEnabled) {
      return getAccountCapabilityRequestFilters(accountReference)
    }
  }, [accountReference, isProfileLoading, profile?.loggedIn])

  const {
    data: accountCapabilitiesData,
    isFetching: isFetchingAccountCapabilities,
    error: fetchCapabilitiesError,
  } = useBulkCapabiltiesQuery(accountCapabilityRequest, !!accountCapabilityRequest)

  const accountCapabilitiesDetails = useMemo(() => {
    if (!!accountCapabilitiesData && !!accountReference) {
      return getAccountCapabilities(accountReference, accountCapabilitiesData)
    }
  }, [accountReference, accountCapabilitiesData])

  useEffect(() => {
    if (!!accountCapabilitiesDetails && !isFetchingAccountCapabilities) {
      setAccountCapabilities(accountCapabilitiesDetails)
    }
  }, [accountCapabilitiesDetails, isFetchingAccountCapabilities])

  useEffect(() => {
    setCapabilitiesError(fetchCapabilitiesError)
  }, [fetchCapabilitiesError, setCapabilitiesError])

  return {
    accountCapabilities,
    isFetchingAccountCapabilities,
  }
}
